import { useI18n } from "@/i18n/I18nContext";
import { Clock, MapPin, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WorkoutContentButton } from "@/components/WorkoutContentDialog";

const LOCALE = { es: "es-ES", en: "en-US", fr: "fr-FR" };
const PANAMA_TZ = "America/Panama";

export function fmtDate(iso, lang) {
  const d = new Date(iso);
  return d.toLocaleDateString(LOCALE[lang] || "es-ES", { weekday: "short", day: "numeric", month: "short", timeZone: PANAMA_TZ });
}
export function fmtTime(iso, lang) {
  return new Date(iso).toLocaleTimeString(LOCALE[lang] || "es-ES", { hour: "2-digit", minute: "2-digit", timeZone: PANAMA_TZ });
}

export function SessionCard({ session, onBook, onWaitlist, onCancel, compact }) {
  const { t, tl, lang } = useI18n();
  const ct = session.class_type || {};
  const isPilates = session.universe === "pilates";
  const spots = session.spots_left ?? 0;
  const full = spots <= 0;
  const mine = session.booked_by_me;
  const accent = isPilates ? "hsl(217 91% 60%)" : "hsl(355 85% 55%)";

  return (
    <div
      data-testid={`session-card-${session.session_id}`}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-transform hover:-translate-y-0.5"
    >
      <span className="absolute left-0 top-0 h-full w-1.5" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>
            {isPilates ? "Pilates" : "Fitness"}
          </span>
          <h3 className="mt-1 truncate font-display text-lg font-semibold">{tl(ct.name) || "Clase"}</h3>
          {session.level && session.level !== "all" && (
            <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${accent}22`, color: accent }}>
              {t("common.level")} {session.level}
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="font-display text-sm font-semibold">{fmtTime(session.start, lang)}</div>
          <div className="text-xs text-muted-foreground">{fmtDate(session.start, lang)}</div>
        </div>
      </div>

      {!compact && tl(ct.description) && (
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{tl(ct.description)}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {session.coach?.name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{session.coach.name}</span>}
        {ct.duration_min && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{ct.duration_min} {t("common.min")}</span>}
        {session.room?.name && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{session.room.name}</span>}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className={`text-xs font-semibold ${mine ? "text-emerald-500" : full ? "text-destructive" : "text-foreground"}`}>
          {mine ? tl({ es: "Reservado ✓", en: "Booked ✓", fr: "Réservé ✓" }) : full ? t("common.full") : `${spots} ${t("common.spotsLeft")}`}
        </span>
        {mine ? (
          <Button size="sm" variant="outline" data-testid={`cancel-btn-${session.session_id}`}
            onClick={() => onCancel?.(session)} className="rounded-full border-destructive text-destructive hover:bg-destructive/10">
            {tl({ es: "Cancelar", en: "Cancel", fr: "Annuler" })}
          </Button>
        ) : full ? (
          <Button size="sm" variant="outline" data-testid={`waitlist-btn-${session.session_id}`}
            onClick={() => onWaitlist?.(session)} className="rounded-full">
            {t("common.joinWaitlist")}
          </Button>
        ) : (
          <Button size="sm" data-testid={`book-btn-${session.session_id}`}
            onClick={() => onBook?.(session)} className="rounded-full">
            {t("common.book")}
          </Button>
        )}
      </div>

      {mine && (
        <div className="mt-3">
          <WorkoutContentButton session={session} className="w-full justify-center" />
        </div>
      )}
    </div>
  );
}
